import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  TDefaultValue,
  TSelectOption,
} from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { AsyncSearchableSelect } from '@/shared/components/ui/form/components/searchable-select/variant/async/async-searchable-select';
import { GroupAdditionalInfo } from '@/shared/features/groups/components/group-select/group-additional-info/group-additional-info';
import { GroupSelectLabel } from '@/shared/features/groups/components/group-select/group-select-label/group-select-label';
import { TResourceGroupSelectProps } from '@/shared/features/groups/components/group-select/group-select.types';
import { useReadResourceGroupsForSelectQuery } from '@/shared/redux/api/assets/groups/groups-api';
import {
  ReadResourceGroupsForSelectQuery,
  ResourceGroupForSelectFragment,
} from '@/shared/types/types.generated';

const NO_GROUP_OPTION_VALUE = '';

export const GroupSelect = <TFieldValue extends FieldValues>({
  name,
  disabled = false,
  parentGroupId,
  getItemId,
  label,
  required,
  groupFilter,
  onSelectChange,
  multiple,
  hint,
  showNoGroupOption = true,
  createOption,
  additionalOptions = [],
  ...rest
}: TResourceGroupSelectProps<TFieldValue, string>) => {
  const { t } = useTranslation();
  const displayLabel = label ?? t('groups:drawer.create_group.group_location');

  const createGroupOption = (
    group: ResourceGroupForSelectFragment,
  ): TSelectOption<string> => {
    return createOption
      ? createOption(group)
      : {
          value: group.groupId,
          label: group.name,
          additionalInfo: <GroupAdditionalInfo group={group} />,
        };
  };

  const createNoGroupOption = (): TSelectOption<string> => ({
    label: t('groups:no_group'),
    value: NO_GROUP_OPTION_VALUE,
  });

  const getDefaultValue = (): TDefaultValue<TFieldValue> | undefined =>
    parentGroupId ? (parentGroupId as TDefaultValue<TFieldValue>) : undefined;

  const filterGroups = (
    groups: ResourceGroupForSelectFragment[],
  ): ResourceGroupForSelectFragment[] => {
    return groupFilter ? groups.filter(groupFilter) : groups;
  };

  const createGroupLocationOptions = (
    data?: ReadResourceGroupsForSelectQuery,
  ): TSelectOption<string>[] => {
    if (!data) {
      return [];
    }

    const allGroups = data.readAllResourceGroups.items;
    const filteredGroups = filterGroups(allGroups);
    const groupOptions = filteredGroups.map(createGroupOption);
    let options = groupOptions;

    if (showNoGroupOption) {
      options = [createNoGroupOption(), ...groupOptions];
    }

    return [...additionalOptions, ...options];
  };

  return (
    <AsyncSearchableSelect<
      TFieldValue,
      ReadResourceGroupsForSelectQuery,
      string
    >
      name={name}
      defaultValue={getDefaultValue()}
      disabled={disabled}
      customLabel={(option) => <GroupSelectLabel option={option} />}
      label={displayLabel}
      placeholder={t('groups:select_group')}
      getItemId={getItemId}
      optionProps={{
        useRTKQuery: useReadResourceGroupsForSelectQuery,
        transformData: createGroupLocationOptions,
      }}
      required={required}
      hint={hint}
      onSelectChange={onSelectChange}
      multiple={multiple}
      {...rest}
    />
  );
};
